import React, { useEffect, useRef, useState } from 'react';
import { ArchiveRestore, ChevronLeft, ChevronRight, RefreshCw, Search, Trash2 } from 'lucide-react';
import { useUser } from '../../../context/UserContext';
import { appConfirm, appPrompt } from '../../../services/appDialogs';
import { fetchRecycleBin, permanentlyDeleteRecycleBinEntry, restoreRecycleBinEntry } from '../../../services/adminService';
import { AppSelect } from '../../ui/AppSelect';
import { DangerButton, EmptyText, ErrorNotice, Panel, SecondaryButton } from '../adminUi';

const TYPES = [
  ['project', '项目'], ['character', '角色'], ['scene', '场景'], ['prop', '道具'],
  ['resource', '媒体资源'], ['agent_resource', '旧版媒体资源'], ['visual_reference', '视觉参考'],
  ['character_variant', '角色变体'], ['scene_variant', '场景变体'], ['prop_variant', '道具变体'],
];
const TYPE_LABELS = Object.fromEntries(TYPES);
const EMPTY_PAGE = { content: [], totalElements: 0, totalPages: 0, number: 0, size: 20 };
const inputStyle = { backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' };
const readableTime = value => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString('zh-CN', { hour12: false });
};
const errorText = (error, fallback) => {
  const status = Number(error?.status || error?.code);
  if (status === 403) return '当前账号没有回收站管理权限。';
  if (status === 404) return '该记录已被处理，请刷新列表。';
  if (status === 409) return '当前记录暂时无法处理，关联项目或资产状态可能已改变。请刷新后重试。';
  return fallback;
};

export default function RecycleBinTab({ onProjectsChanged }) {
  const { isAdmin, userId, token, role } = useUser() || {};
  const authScope = JSON.stringify([userId, token, role]);
  const [filters, setFilters] = useState({ type: '', keyword: '', page: 0 });
  const [search, setSearch] = useState('');
  const [revision, setRevision] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState('');
  const busyRef = useRef(false);
  const mounted = useRef(true);
  const allowed = useRef(isAdmin);
  allowed.current = isAdmin;
  const session = useRef(authScope);
  session.current = authScope;
  const requestKey = JSON.stringify([filters, revision, authScope]);
  const current = result?.key === requestKey ? result : null;
  const loading = Boolean(isAdmin) && !current;
  const page = current?.data || EMPTY_PAGE;
  const disabled = Boolean(busy) || loading;

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (!isAdmin) return undefined;
    const controller = new AbortController();
    setError('');
    fetchRecycleBin({ ...filters, size: 20 }, { signal: controller.signal }).then(data => {
      if (controller.signal.aborted) return;
      if (!data || !Array.isArray(data.content)) throw new Error('Invalid recycle bin page');
      setResult({ key: requestKey, data: { ...EMPTY_PAGE, ...data } });
    }).catch(cause => {
      if (controller.signal.aborted) return;
      setResult({ key: requestKey, data: EMPTY_PAGE });
      setError(errorText(cause, '回收站加载失败，请重试。'));
    });
    return () => controller.abort();
  }, [filters, requestKey, isAdmin]);

  const actOnEntry = async (entry, permanent) => {
    if (!allowed.current || busyRef.current || loading || entry?.id == null) return;
    busyRef.current = true;
    const confirmationSession = session.current;
    const isCurrentSession = () => mounted.current && allowed.current && session.current === confirmationSession;
    setBusy(`${permanent ? 'delete' : 'restore'}:${entry.id}`);
    setError('');
    setNotice('');
    try {
      const label = `${TYPE_LABELS[entry.type] || entry.type || '记录'}「${entry.name || '未命名'}」`;
      if (!(await appConfirm(permanent
        ? `确定永久删除${label}（回收编号 ${entry.id}）吗？此操作会永久删除数据库记录，无法恢复；COS 共享文件暂不清理。`
        : `恢复${label}（回收编号 ${entry.id}）后，会重新出现在原有列表中。确定恢复吗？`, {
        title: permanent ? '永久删除确认' : '恢复记录',
        confirmLabel: permanent ? '继续核对编号' : '恢复',
        variant: permanent ? 'danger' : 'primary',
      }))) return;
      if (!isCurrentSession()) return;
      if (permanent) {
        const confirmation = await appPrompt(`请输入回收编号 ${entry.id}，确认永久删除${label}。`, '', { title: '二次确认永久删除', confirmLabel: '永久删除', placeholder: `回收编号 ${entry.id}`, variant: 'danger' });
        if (!isCurrentSession() || confirmation == null) return;
        if (confirmation.trim() !== String(entry.id)) {
          setError('回收编号不匹配，未执行永久删除。');
          return;
        }
        await permanentlyDeleteRecycleBinEntry(entry.id, confirmation.trim());
      } else {
        await restoreRecycleBinEntry(entry.id);
      }
      if (!isCurrentSession()) return;
      setNotice(permanent ? '已永久删除数据库记录。' : '已恢复，可在原有列表中查看。');
      if (page.content.length === 1 && filters.page > 0) setFilters(previous => ({ ...previous, page: previous.page - 1 }));
      else setRevision(value => value + 1);
      if (entry.type === 'project') Promise.resolve().then(() => onProjectsChanged?.()).catch(() => {});
    } catch (cause) {
      if (isCurrentSession()) setError(errorText(cause, permanent ? '永久删除未确认成功，请刷新核实后再操作。' : '恢复未确认成功，请刷新核实后再操作。'));
    } finally {
      busyRef.current = false;
      if (mounted.current) setBusy('');
    }
  };

  if (!isAdmin) return <ErrorNotice message="仅管理员可以查看和管理回收站。" />;
  return <div className="space-y-4">
    <Panel title="回收站" sub="仅管理员可恢复或永久删除" right={<SecondaryButton disabled={disabled} onClick={() => setRevision(value => value + 1)} className="inline-flex items-center gap-1.5"><RefreshCw size={13} />刷新</SecondaryButton>}>
      <form className="flex flex-wrap items-end gap-3 mb-4" onSubmit={event => { event.preventDefault(); if (!busyRef.current) { setNotice(''); setFilters(previous => ({ ...previous, keyword: search.trim(), page: 0 })); } }}>
        <label className="text-label flex flex-col gap-1.5">类型<AppSelect aria-label="回收记录类型" value={filters.type} disabled={Boolean(busy)} onChange={event => { setNotice(''); setFilters(previous => ({ ...previous, type: event.target.value, page: 0 })); }} className="h-8 px-2 rounded-md text-xs" style={inputStyle}><option value="">全部类型</option>{TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</AppSelect></label>
        <label className="text-label flex flex-col gap-1.5">搜索<input aria-label="搜索回收记录" value={search} disabled={Boolean(busy)} onChange={event => setSearch(event.target.value)} placeholder="名称 / 编号" className="h-8 px-2.5 rounded-md text-xs w-56 max-w-full" style={inputStyle} /></label>
        <button type="submit" disabled={Boolean(busy)} className="h-8 px-3 rounded-md text-xs border inline-flex items-center gap-1.5 disabled:opacity-50" style={inputStyle}><Search size={13} />搜索</button>
      </form>
      {error ? <div role="alert"><ErrorNotice message={error} /></div> : null}
      {notice ? <p role="status" className="text-xs mb-3" style={{ color: 'var(--color-success)' }}>{notice}</p> : null}
      <div className="overflow-x-auto" aria-busy={loading}>
        <table className="w-full text-sm"><thead><tr className="text-label text-left">{['回收编号', '类型 / 名称', '所属项目', '删除人', '删除时间', '操作'].map(title => <th scope="col" key={title} className="font-medium pb-2.5 pr-4 whitespace-nowrap">{title}</th>)}</tr></thead>
          <tbody>{loading ? <tr><td colSpan={6}><div role="status" className="py-10 text-xs text-center">正在加载回收记录…</div></td></tr> : !page.content.length ? <tr><td colSpan={6}><EmptyText text={error ? '未能读取回收记录' : '暂无回收记录'} /></td></tr> : page.content.map(entry => <tr key={entry.id} style={{ borderTop: '1px solid var(--color-border-secondary)' }}>
            <td className="py-3 pr-4 text-xs font-mono-num">{entry.id}</td>
            <td className="py-3 pr-4"><div className="text-xs font-medium break-words max-w-sm">{entry.name || '未命名'}</div><div className="text-2xs mt-1" style={{ color: 'var(--color-text-muted)' }}>{TYPE_LABELS[entry.type] || entry.type || '其他'} · 原编号 {entry.targetId ?? '—'}</div></td>
            <td className="py-3 pr-4 text-xs">{entry.projectName || (entry.projectId != null ? `项目 #${entry.projectId}` : '—')}{entry.projectName && entry.projectId != null ? <div className="text-2xs mt-1" style={{ color: 'var(--color-text-muted)' }}>#{entry.projectId}</div> : null}</td>
            <td className="py-3 pr-4 text-xs">{entry.deletedBy || '—'}</td><td className="py-3 pr-4 text-2xs whitespace-nowrap font-mono-num">{readableTime(entry.deletedAt)}</td>
            <td className="py-3 pr-4"><div className="flex gap-2 whitespace-nowrap"><SecondaryButton disabled={disabled} onClick={() => actOnEntry(entry, false)} className="inline-flex items-center gap-1"><ArchiveRestore size={12} />{busy === `restore:${entry.id}` ? '处理中…' : '恢复'}</SecondaryButton><DangerButton disabled={disabled} onClick={() => actOnEntry(entry, true)} className="inline-flex items-center gap-1"><Trash2 size={12} />{busy === `delete:${entry.id}` ? '处理中…' : '永久删除'}</DangerButton></div></td>
          </tr>)}</tbody>
        </table>
      </div>
      <footer className="flex flex-wrap items-center justify-between gap-3 mt-4 text-xs" style={{ color: 'var(--color-text-muted)' }}><span>共 {page.totalElements} 条</span><nav aria-label="回收站分页" className="flex items-center gap-3"><SecondaryButton disabled={disabled || filters.page === 0} onClick={() => setFilters(previous => ({ ...previous, page: previous.page - 1 }))}><span className="inline-flex items-center gap-1"><ChevronLeft size={13} />上一页</span></SecondaryButton><span>{page.totalPages ? filters.page + 1 : 0} / {page.totalPages}</span><SecondaryButton disabled={disabled || filters.page + 1 >= page.totalPages} onClick={() => setFilters(previous => ({ ...previous, page: previous.page + 1 }))}><span className="inline-flex items-center gap-1">下一页<ChevronRight size={13} /></span></SecondaryButton></nav></footer>
    </Panel>
  </div>;
}
